import TextCollapse from '@liveauctioneers/caterwaul-components/lib/TextCollapse/TextCollapse';
import { AddressDisplay } from '@liveauctioneers/hammer-ui-core/addressDisplay';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { H2 } from '@liveauctioneers/hammer-ui-core/text';
import * as React from 'react';

import AuctioneerInfo from '@/components/AuctioneerInfo/AuctioneerInfo';
import BidIncrements from '@/components/BidIncrements/BidIncrements';
import BuyersPremium from '@/components/BuyersPremium/BuyersPremium';
import { CuratorInfo } from '@/components/CuratorInfo/CuratorInfo';
import { DescriptionSection } from '@/components/ItemDescription/DescriptionSection';
import useIsNarrowerThan from '@/hooks/useIsNarrowerThan';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
    getCatalogAddress,
    getCatalogIsAutopay,
    getCatalogPreview,
    getCatalogSellerId,
    getCatalogSellerName,
    getCatalogTitle,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import {
    getCatalogDetailCustomTerms,
    getCatalogDetailTerms,
} from '@/redux/modules/catalog/detail/catalogDetail.selectors';
import { openSendSellerMessageModal } from '@/redux/modules/modal';
import { CustomTerm } from '@/types/CustomTerm';
import { safelyRender } from '@/utils/safelyRender';

const MIN_NUMBER_OF_TEXT_COLLAPSE_PIXELS = 168;

type Props = {
    catalogId: number;
};

export const CatalogFooter = ({ catalogId }: Props) => {
    const dispatch = useAppDispatch();
    const { formatMessage } = useIntl();

    const address = useAppSelector((state) => getCatalogAddress(state, catalogId));
    const isAutopay = useAppSelector((state) => getCatalogIsAutopay(state, catalogId));
    const sellerName = useAppSelector((state) => getCatalogSellerName(state, catalogId));
    const title = useAppSelector((state) => getCatalogTitle(state, catalogId));
    const sellerId = useAppSelector((state) => getCatalogSellerId(state, catalogId));
    const preview = useAppSelector((state) => getCatalogPreview(state, catalogId));
    const terms = useAppSelector((state) => getCatalogDetailTerms(state, catalogId));
    const customTerms = useAppSelector((state) => getCatalogDetailCustomTerms(state, catalogId));

    const showCurators = useIsNarrowerThan(1100);

    const safeTerms = safelyRender({ html: terms, wrapper: 'div' });
    const { city, country: countryCode, postalCode, state: stateCode } = address;

    const showSendSellerMessageModal = React.useCallback(() => {
        const messageDetails = {
            catalogId,
            title,
        };

        const subject = `${formatMessage({ id: 'auction' })}: ${title}`;

        dispatch(openSendSellerMessageModal(messageDetails, sellerId, sellerName, subject));
    }, [catalogId, dispatch, formatMessage, sellerId, sellerName, title]);

    const filterTerms = React.useCallback(
        (customTerm: CustomTerm) => {
            //before 12/15/2021 we inserted autopay terms for every catalog in the autpay betas, even if the catalog wasn't using autopay. So this is here to account for that incorrect data.
            if (customTerm.title === 'Autopay' && !isAutopay) {
                return false;
            }
            return customTerm.data !== '';
        },
        [isAutopay]
    );

    const mapCustomTerms = React.useCallback((customTerm: CustomTerm) => {
        const safeTerms = safelyRender({
            html: customTerm.data,
            wrapper: 'div',
        });
        return (
            <DescriptionSection
                body={
                    <TextCollapse
                        content={safeTerms}
                        minHeightPx={MIN_NUMBER_OF_TEXT_COLLAPSE_PIXELS}
                    />
                }
                header={<span>{customTerm.title}</span>}
                key={customTerm.id}
            />
        );
    }, []);

    return (
        <div
            className="mb-md w-[calc(100%-40px)] max-w-[1200px] self-center rounded-sm pb-md pt-sm"
            id="catalog-footer-header"
        >
            <H2
                className="my-md border-b-2 border-solid border-border-secondary pb-md"
                data-testid="catologFooterTitle"
            >
                <FormattedMessage id="auction_details" />
            </H2>
            <div className="flex flex-col smMin:flex-row">
                <div className="mr-md flex flex-1 basis-[35%] flex-col xsMax:mb-0 xsMax:basis-auto">
                    <AuctioneerInfo
                        className="mb-xl"
                        sellerId={sellerId}
                        showSendSellerMessageModal={showSendSellerMessageModal}
                    />
                    {showCurators && <CuratorInfo catalogId={catalogId} />}
                    <BidIncrements catalogId={catalogId} />
                </div>
                <div className="flex flex-1 basis-[65%] flex-col xsMax:basis-auto">
                    {Boolean(preview) && (
                        <DescriptionSection
                            body={
                                <>
                                    {safelyRender({
                                        html: preview,
                                        wrapper: 'div',
                                    })}
                                    {(Boolean(city) || Boolean(countryCode)) && (
                                        <AddressDisplay
                                            address={{
                                                // we hide the street address to protect house information
                                                address1: '',
                                                city,
                                                countryCode,
                                                postalCode,
                                                state: stateCode,
                                            }}
                                            className="mt-xs"
                                        />
                                    )}
                                </>
                            }
                            header={<FormattedMessage id="catalogPage.footer.preview" />}
                        />
                    )}
                    <BuyersPremium catalogId={catalogId} />
                    <DescriptionSection
                        body={
                            <TextCollapse
                                content={safeTerms}
                                minHeightPx={MIN_NUMBER_OF_TEXT_COLLAPSE_PIXELS}
                            />
                        }
                        header="terms_and_conditions"
                    />
                    {customTerms?.filter(filterTerms).map(mapCustomTerms)}
                </div>
            </div>
        </div>
    );
};
